import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, Typography, Avatar, Tag, Modal, Input, Button, List, Alert, Badge, Space } from "antd";
import {
  ClockCircleOutlined,
  FireFilled,
  UserOutlined,
  ClearOutlined,
  SoundOutlined,
  SoundFilled,
  WifiOutlined,
  DisconnectOutlined,
  InboxOutlined,
  ScanOutlined,
  DropboxOutlined,
  ThunderboltOutlined,
  SafetyCertificateOutlined,
  HomeOutlined,
  CalendarOutlined,
  BarChartOutlined,
  InfoCircleOutlined,
  BulbOutlined,
  CaretRightOutlined,
  WarningOutlined,
  UnorderedListOutlined,
  CheckCircleFilled,
  BarcodeOutlined,
  DeleteOutlined,
  PlusOutlined,
  LoadingOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@ant-design/icons";
import moment from "moment";
import debounce from "lodash.debounce";
import Cookies from "js-cookie";
import config from "../config";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { ProgressBar } from "primereact/progressbar";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "./PackingScreen.improved.css";

const { Title, Text } = Typography;

const getPackingAuthConfig = () => {
  const token = Cookies.get('token') || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};
const hasAdminRole = () => {
  try {
    const token = Cookies.get('token') || localStorage.getItem('token');
    if (!token) return false;
    const encodedPayload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(encodedPayload));
    return payload.role === 'admin';
  } catch (error) {
    return false;
  }
};

const formatPackingDimensions = (dimensions = {}) => {
  if (dimensions.display) return dimensions.display;
  const values = [dimensions.length, dimensions.width, dimensions.height].filter((value) => value !== null && value !== undefined && value !== '');
  if (!values.length && dimensions.thickness) values.push(dimensions.thickness);
  return values.length ? `${values.join(' × ')} ${dimensions.unit || 'CM'}` : 'Dimensions not set';
};

// Audio feedback function
const playAudioFeedback = (type) => {
  if (typeof window === 'undefined') return;

  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    switch (type) {
      case 'success':
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
        break;
      case 'error':
        oscillator.frequency.setValueAtTime(300, audioContext.currentTime);
        oscillator.type = 'sawtooth';
        gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.4);
        break;
      case 'warning':
        oscillator.frequency.setValueAtTime(500, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(400, audioContext.currentTime + 0.1);
        oscillator.type = 'triangle';
        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.2);
        break;
      default:
        oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
    }
  } catch (error) {
    console.warn('Audio feedback error:', error);
  }
};

// Text to Speech function
const speakFeedback = (text) => {
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    window.speechSynthesis.speak(utterance);
  }
};

const PackingScreen = ({ userName, darkTheme = false }) => {
  const [orderID, setOrderID] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);
  const [scannedProducts, setScannedProducts] = useState({});
  const [isOrderPacked, setIsOrderPacked] = useState(false);
  const [statusMessage, setStatusMessage] = useState("Ready to scan Order ID or AWB Code");
  const [packingStage, setPackingStage] = useState("INITIAL");
  const [scanBuffer, setScanBuffer] = useState("");
  const [lastKeyTime, setLastKeyTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(moment().format("HH:mm:ss"));
  const [temperature, setTemperature] = useState("30°C");
  const [isHoldModalVisible, setIsHoldModalVisible] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [isOverrideModalVisible, setIsOverrideModalVisible] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isNetworkLost, setIsNetworkLost] = useState(false);
  const [showNetworkBanner, setShowNetworkBanner] = useState(false);
  const [boxScans, setBoxScans] = useState([]);
  const [boxBarcode, setBoxBarcode] = useState("");
  const [boxScanStatus, setBoxScanStatus] = useState("WAITING FOR PRODUCTS");
  const [boxScanBusy, setBoxScanBusy] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [extraBoxMode, setExtraBoxMode] = useState(false);
  const [boxCorrection, setBoxCorrection] = useState(null);
  const [boxCorrectionReason, setBoxCorrectionReason] = useState("");
    const [lockedOrderInfo, setLockedOrderInfo] = useState(null);
    const [adminReleaseReason, setAdminReleaseReason] = useState("");
    const [adminReleaseBusy, setAdminReleaseBusy] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({ scheduledForPackingToday: 0, todayPickedCount: 0, todayPendingCount: 0, todayOnHoldCount: 0, todayStats: { todayCompletedCount: 0 } });
  const [dashboardInventory, setDashboardInventory] = useState({ boxesUsedToday: 0, activePackingSessions: 0, averagePackingDurationMs: 0, boxInventory: [], lowStockMaterials: 0, outOfStockMaterials: 0 });
  const [availableBoxMaterials, setAvailableBoxMaterials] = useState([]);
  const [selectedBoxMaterials, setSelectedBoxMaterials] = useState([]);
  const [loadingBoxMaterials, setLoadingBoxMaterials] = useState(false);

  const toast = useRef(null);
  const boxBarcodeRef = useRef(null);
  const packingRootRef = useRef(null);
  const boxScanInFlightRef = useRef(false);

  // Dark theme CSS classes
  const themeClass = darkTheme ? 'dark-theme' : 'light-theme';
  const containerClass = darkTheme ? 'dark-container' : 'light-container';
  const cardClass = darkTheme ? 'dark-card' : 'light-card';
  const textClass = darkTheme ? 'dark-text' : 'light-text';
  const canAdminRelease = hasAdminRole();

  const loadPackingDashboard = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/packing/overview`, getPackingAuthConfig());
      const overview = response.data || {};
      setDashboardStats(overview);
      setDashboardInventory((previous) => ({
        ...previous,
        activePackingSessions: overview.activePackingSessions || 0,
        averagePackingDurationMs: overview.averagePackingDurationMs || 0,
      }));
    } catch (error) {
      console.error('Packing dashboard refresh error:', error);
    }
  };

  const loadAvailableBoxMaterials = async () => {
    setLoadingBoxMaterials(true);
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/packing-materials`, getPackingAuthConfig());
      const materials = (response.data.materials || []).filter((material) =>
        material.materialType === 'Box' && material.status === 'Active'
      );
      setAvailableBoxMaterials(materials);
      setSelectedBoxMaterials([]);
      setDashboardInventory((previous) => ({
        ...previous,
        boxInventory: materials.map((material) => ({
          materialId: material._id,
          materialName: material.materialName,
          barcode: material.barcode,
          currentStock: Number(material.currentStock || 0),
          minimumStock: Number(material.minimumStock || 0),
          status: Number(material.currentStock || 0) === 0 ? 'OUT OF STOCK' : Number(material.currentStock || 0) < Number(material.minimumStock || 0) ? 'LOW STOCK' : 'IN STOCK',
        })),
      }));
    } catch (error) {
      console.error('Packing material list refresh error:', error);
      setAvailableBoxMaterials([]);
    } finally {
      setLoadingBoxMaterials(false);
    }
  };

  const updateDashboardBoxStock = (material, currentStock) => {
    if (!material?._id) return;
    setDashboardInventory((previous) => {
      const currentInventory = previous.boxInventory || [];
      const inventoryItem = {
        materialId: material._id,
        materialName: material.materialName,
        barcode: material.barcode,
        currentStock: Number(currentStock || 0),
        minimumStock: Number(material.minimumStock || 0),
        status: Number(currentStock || 0) === 0 ? 'OUT OF STOCK' : Number(currentStock || 0) < Number(material.minimumStock || 0) ? 'LOW STOCK' : 'IN STOCK',
      };
      const exists = currentInventory.some((item) => String(item.materialId) === String(material._id));
      return {
        ...previous,
        boxInventory: exists
          ? currentInventory.map((item) => String(item.materialId) === String(material._id) ? inventoryItem : item)
          : [...currentInventory, inventoryItem],
      };
    });
  };

  useEffect(() => {
    loadPackingDashboard();
    loadAvailableBoxMaterials();
  }, []);

  // Network detection
  useEffect(() => {
    const handleOnline = () => {
      setIsNetworkLost(false);
      setShowNetworkBanner(false);
    };

    const handleOffline = () => {
      setIsNetworkLost(true);
      setShowNetworkBanner(true);
      toast.current.show({
        severity: "warn",
        summary: "Network Lost",
        detail: "You are offline. Some features may be limited.",
        life: 3000,
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    setIsNetworkLost(!navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Auto-hide network banner after 5 seconds
  useEffect(() => {
    let timer;
    if (showNetworkBanner && !isNetworkLost) {
      timer = setTimeout(() => {
        setShowNetworkBanner(false);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [showNetworkBanner, isNetworkLost]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(moment().format("HH:mm:ss"));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const updateFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === packingRootRef.current);
    };
    document.addEventListener('fullscreenchange', updateFullscreenState);
    return () => document.removeEventListener('fullscreenchange', updateFullscreenState);
  }, []);

  // Keyboard scanner input handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      const currentTime = new Date().getTime();
      const key = e.key;

      if (e.target?.closest?.('input, textarea, [contenteditable="true"]')) return;
      if (e.ctrlKey || e.altKey || e.metaKey || key.length > 1) return;

      setScanBuffer((prev) => prev + key);
      setLastKeyTime(currentTime);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lastKeyTime, scanBuffer]);

  // Debounced scanner processing
  useEffect(() => {
    const debouncedProcessScannedData = debounce(() => {
      if (scanBuffer.length > 0) {
        processScannedData(scanBuffer);
        setScanBuffer("");
      }
    }, 200);

    debouncedProcessScannedData();
    return () => debouncedProcessScannedData.cancel();
  }, [scanBuffer]);

  // Clear Order Function
  const clearOrder = async () => {
    if (orderDetails?.packingSessionOpen) {
      try {
        await axios.post(
          `${config.apiBaseUrl}/packing/order-session/${encodeURIComponent(orderID)}/cancel`,
          { packingSessionId: orderDetails.packingSessionId },
          getPackingAuthConfig()
        );
      } catch (error) {
        toast.current.show({ severity: 'error', summary: 'Session not ended', detail: error.response?.data?.message || 'Could not safely release this packing session.', life: 3500 });
        return;
      }
    }
    setOrderDetails(null);
    setOrderID("");
    setScannedProducts({});
    setBoxScans([]);
    setSelectedBoxMaterials([]);
    setBoxBarcode("");
    setExtraBoxMode(false);
    setIsOrderPacked(false);
    setStatusMessage("Order cleared. Ready to scan Order ID or AWB Code");
    setPackingStage("INITIAL");
    setStartTime(null);
    setHoldReason("");
    setOverrideReason("");
    setIsHoldModalVisible(false);
    setIsOverrideModalVisible(false);

    if (audioEnabled) playAudioFeedback('warning');

    toast.current.show({
      severity: "info",
      summary: "Order Cleared",
      detail: "Current order has been cleared. You can now scan a new order.",
      life: 3000,
    });
  };

  // Toggle audio feedback
  const toggleAudioFeedback = () => {
    setAudioEnabled(!audioEnabled);
    toast.current.show({
      severity: "info",
      summary: "Audio Feedback",
      detail: `Audio feedback ${!audioEnabled ? 'enabled' : 'disabled'}`,
      life: 2000,
    });
  };

  // NOTE: This is a purely visual/UX affordance for the new "Session Not Started"
  // landing screen. There is no session-open handler or session state in the
  // original component (packing simply begins automatically the moment a
  // barcode scanner sends the first Order ID / AWB code via the existing
  // keydown listener). This function does not add any new business logic,
  // state, or API calls — it only reuses the existing toast + audio helpers
  // to prompt the operator to scan.
  const handleOpenSessionClick = () => {
    if (audioEnabled) playAudioFeedback('default');
    toast.current.show({
      severity: "info",
      summary: "Ready to Scan",
      detail: "Scan an Order ID or AWB code with your scanner to begin packing.",
      life: 3000,
    });
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (packingRootRef.current?.requestFullscreen) {
        await packingRootRef.current.requestFullscreen();
      }
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Fullscreen unavailable', detail: 'Your browser could not open fullscreen mode.', life: 2500 });
    }
  };

  const processScannedData = (scannedValue) => {
    const trimmedValue = scannedValue.trim();

    if (!orderDetails) {
      fetchOrderDetails(trimmedValue);
    } else {
      if (packingStage === "ON_HOLD") {
        if (audioEnabled) {
          playAudioFeedback('error');
          speakFeedback("Order is on hold");
        }
        toast.current.show({
          severity: "error",
          summary: "Action Blocked",
          detail: "This order is on hold. You cannot scan products.",
          life: 3000,
        });
        return;
      }
      if (packingStage === "CANCELLED") {
        if (audioEnabled) {
          playAudioFeedback('error');
          speakFeedback("Order is cancelled");
        }
        toast.current.show({
          severity: "error",
          summary: "Action Blocked",
          detail: "This order is cancelled. You cannot scan products.",
          life: 3000,
        });
        return;
      }
      // Packing-material barcodes are exactly 16 digits. Route them to the
      // box handler before the normal product-scan path.
      if (/^\d{16}$/.test(trimmedValue)) {
        handleBoxScan(trimmedValue);
        return;
      }
      const isAwbCode = orderDetails.shipments?.some(
        (shipment) => shipment.awb_code === trimmedValue
      );
      if ((trimmedValue === orderID || isAwbCode) && isPackingComplete()) {
        completePacking();
      } else {
        handleProductScan(trimmedValue);
      }
    }
  };

  const fetchOrderDetails = (scannedValue) => {
    setStatusMessage(`Fetching order details for ${scannedValue}...`);
    setPackingStage("SCANNING");
    const url = `${config.apiBaseUrl}/packing/order-session/open`;

    axios
      .post(url, { scannedValue }, getPackingAuthConfig())
      .then((res) => {
        if (res.data.order && res.data.order.packed_status === "Completed") {
          if (audioEnabled) playAudioFeedback('warning');
          toast.current.show({
            severity: "warn",
            summary: "Warning",
            detail: "This order is already packed.",
            life: 3000,
          });
          setIsOrderPacked(true);
          setStatusMessage("Order already packed. Scan a new Order ID or AWB Code.");
          setPackingStage("COMPLETED");
        } else if (res.data.order && res.data.order.packed_status === "Overridden") {
          if (audioEnabled) playAudioFeedback('warning');
          toast.current.show({
            severity: "warn",
            summary: "Warning",
            detail: "This order has been overridden.",
            life: 3000,
          });
          setIsOrderPacked(true);
          setStatusMessage("Order overridden. Scan a new Order ID or AWB Code.");
          setPackingStage("OVERRIDDEN");
        } else if (res.data.order && (res.data.order.status === "On Hold" || res.data.order.packed_status === "Hold")) {
          if (audioEnabled) {
            playAudioFeedback('error');
            speakFeedback("Order is on hold");
          }
          toast.current.show({
            severity: "error",
            summary: "Order On Hold",
            detail: `Order ${res.data.order.orderID} is on hold. Reason: ${res.data.order.hold_reason || "Marked as Hold"}`,
            life: 3000,
          });
          setOrderDetails(null);
          setOrderID("");
          setStatusMessage(`Order ${res.data.order.orderID} is on hold. Reason: ${res.data.order.hold_reason || "Marked as Hold"}`);
          setPackingStage("INITIAL");
        } else if (res.data.order && res.data.order.packed_status === "Cancelled") {
          if (audioEnabled) {
            playAudioFeedback('error');
            speakFeedback("Order is cancelled");
          }
          toast.current.show({
            severity: "error",
            summary: "Order Cancelled",
            detail: `Order ${res.data.order.orderID} is cancelled. You cannot pack this order.`,
            life: 3000,
          });
          setOrderDetails(null);
          setOrderID("");
          setStatusMessage(`Order ${res.data.order.orderID} is cancelled. Scan a new Order ID or AWB Code.`);
          setPackingStage("INITIAL");
        } else if (res.data.order) {
          const validStatuses = ["PICKUP EXCEPTION", "OUT FOR PICKUP", "PICKUP SCHEDULED", "DELIVERED"];
          const shipments = res.data.order.shipments || [];
          const invalidShipment = shipments.find(
            (shipment) => !validStatuses.includes(shipment.status)
          );

          if (invalidShipment) {
            if (audioEnabled) {
              playAudioFeedback('error');
              speakFeedback("Invalid shipment status");
            }
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail: `Cannot pack order: Shipment status "${invalidShipment.status}" is not allowed. Please cross-check the status.`,
              life: 3000,
            });
            setStatusMessage(
              `Invalid shipment status: ${invalidShipment.status}. Only PICKUP EXCEPTION, OUT FOR PICKUP, Or Out For PICKUP SCHEDULED allowed.`
            );
            setPackingStage("INITIAL");
            return;
          }

          // Merge duplicate products (same updated_id/sku) to handle split quantities from Odoo
          if (res.data.order.products && res.data.order.products.length > 0) {
            const productMap = new Map();
            res.data.order.products.forEach((p) => {
              // Use updated_id (barcode) as primary key, fallback to sku or id
              const key = p.updated_id || p.sku || p.id;
              if (productMap.has(key)) {
                const existing = productMap.get(key);
                existing.quantity += Number(p.quantity || 0);
              } else {
                productMap.set(key, { ...p, quantity: Number(p.quantity || 0) });
              }
            });
            res.data.order.products = Array.from(productMap.values());
          }

          if (audioEnabled) playAudioFeedback('success');
          setOrderDetails(res.data.order);
          setOrderID(res.data.order.orderID);
          setScannedProducts(res.data.reused ? {} : Object.fromEntries((res.data.order.packingSessionProductsScanned || []).map((item) => [item.productId, item.quantity])));
          setBoxScans(res.data.order.packingSessionBoxes || []);
          setSelectedBoxMaterials([]);
          setBoxBarcode("");
          setBoxScanStatus((res.data.order.packingSessionBoxes || []).some((box) => box.status === 'PICKED') ? 'BOX SELECTED' : 'WAITING FOR BOX SCAN');
          setExtraBoxMode(false);
          setIsOrderPacked(false);
          setStatusMessage(`Order ${res.data.order.orderID} scanned successfully`);
          setPackingStage("PACKING");
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: res.data.reused ? "Your active session was restored. Product scanning has restarted." : "Order locked to your packing session. Begin scanning products.",
            life: 3000,
          });
          setStartTime(res.data.order.packingSessionOpenedAt || new Date());
        } else {
          if (audioEnabled) {
            playAudioFeedback('error');
            speakFeedback("Order not found");
          }
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: "No order found.",
            life: 3000,
          });
          setStatusMessage("No order found.");
          setPackingStage("INITIAL");
        }
      })
      .catch((error) => {
        if (error.response?.data?.orderAlreadyOpen) {
          const lock = error.response.data;
          const openedAt = lock.openedAt ? moment(lock.openedAt).format('DD MMM YYYY, hh:mm A') : 'Unknown time';
          setStatusMessage(`Order ${lock.orderId || scannedValue} is already open in ${lock.openedByName || 'another operator'}'s packing session.`);
          setLockedOrderInfo({ ...lock, formattedOpenedAt: openedAt });
        } else if (error.response?.data?.code === 'ORDER_ON_HOLD') {
          setStatusMessage(error.response.data.message);
          toast.current.show({ severity: 'error', summary: 'Order On Hold', detail: error.response.data.message, life: 4000 });
        } else if (error.response?.data?.code === 'INVALID_SHIPMENT_STATUS') {
          setStatusMessage(error.response.data.message);
          toast.current.show({ severity: 'error', summary: 'Invalid Shipment Status', detail: error.response.data.message, life: 4000 });
        } else if (error.response?.status === 401 || error.response?.status === 403) {
          setStatusMessage(error.response?.data?.message || 'Sign in with packing access to open an order.');
          toast.current.show({ severity: 'error', summary: 'Packing Access Required', detail: error.response?.data?.message || 'Sign in with packing access to open an order.', life: 4000 });
        } else if (error.response?.status === 409) {
          setStatusMessage(error.response?.data?.message || 'This order cannot be opened for packing.');
          toast.current.show({ severity: 'warn', summary: 'Order Cannot Be Opened', detail: error.response?.data?.message || 'This order cannot be opened for packing.', life: 3500 });
        } else
        // Check if order might already be packed or if it's a network error
        if (error.response?.status === 404) {
          setStatusMessage("Order not found in database. Ask this admin to re-check.");
          if (audioEnabled) playAudioFeedback('error');
          toast.current.show({
            severity: "error",
            summary: "Order Not Found",
            detail: "Order not found in database. Ask this admin to re-check.",
            life: 3000,
          });
        } else if (error.response?.status === 400) {
          // Order not found - could be already packed or deleted
          setStatusMessage("This order already packed. Scan a new Order ID or AWB Code.");
          if (audioEnabled) playAudioFeedback('warning');
          toast.current.show({
            severity: "warn",
            summary: "Order Already Packed",
            detail: "This order is already packed. Scan a new Order ID or AWB Code.",
            life: 3000,
          });
        } else {
          // Network or other error
          setStatusMessage("Failed to fetch order.");
          if (audioEnabled) playAudioFeedback('error');
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to fetch order. Please try again.",
            life: 3000,
          });
        }
        setPackingStage("INITIAL");
      });
  };

  const parseProductCode = (scannedValue) => {
    if (scannedValue.startsWith("01")) {
      scannedValue = scannedValue.slice(2);
    }
    const productCode = scannedValue.slice(0, 14);
    return productCode;
  };

  const handleProductScan = async (scannedValue) => {
    const productID = parseProductCode(scannedValue);

    const product = orderDetails?.products.find((p) => p.updated_id === productID);

    if (product) {
      const currentQuantity = Number(scannedProducts[productID] || 0);
      if (currentQuantity >= Number(product.quantity || 0)) {
        if (audioEnabled) {
          playAudioFeedback('error');
          speakFeedback("Extra item scanned");
        }
        toast.current.show({ severity: "error", summary: "Error", detail: "Exceeds required quantity.", life: 3000 });
        return;
      }

      try {
        const response = await axios.post(
          `${config.apiBaseUrl}/packing/order-session/product-scan`,
          { orderId: orderID, packingSessionId: orderDetails.packingSessionId, barcode: scannedValue },
          getPackingAuthConfig()
        );
        setScannedProducts(response.data.productsScanned || {});
        if (audioEnabled) playAudioFeedback('success');
        toast.current.show({ severity: "success", summary: "Success", detail: `Scanned ${product.name}`, life: 1800 });
        setStatusMessage(`Packing in progress: Scanned ${product.name}`);
      } catch (error) {
        const detail = error.response?.data?.message || 'Product scan could not be recorded. Scan it again.';
        if (audioEnabled) playAudioFeedback('error');
        toast.current.show({ severity: "error", summary: "Product Scan", detail, life: 3000 });
        setStatusMessage(detail);
      }
    } else {
      if (audioEnabled) {
        playAudioFeedback('error');
        speakFeedback("Wrong product");
      }
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Product not found in order.",
        life: 3000,
      });
      setStatusMessage("Product not found. Continue scanning.");
    }
  };

  const handleBoxScan = async (value = boxBarcode) => {
    if (boxScanInFlightRef.current) return;
    const barcode = String(value || '').trim();
    if (!orderDetails || !orderDetails.packingSessionId) return;
    // Box scanning can happen immediately after the order scan. Completion
    // still validates that all products and at least one box are present.
    if (!/^\d{16}$/.test(barcode)) {
      setBoxScanStatus('ERROR');
      toast.current.show({ severity: 'error', summary: 'INVALID BOX BARCODE', detail: 'Packing material barcodes must be exactly 16 digits.', life: 3000 });
      setBoxBarcode('');
      boxBarcodeRef.current?.focus();
      return;
    }

    boxScanInFlightRef.current = true;
    setBoxScanBusy(true);
    setBoxScanStatus('VALIDATING');
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/packing/material/scan`,
        {
          orderId: orderID,
          packingSessionId: orderDetails.packingSessionId,
          barcode,
          allowRepeat: extraBoxMode || pickedBoxes.length > 0,
        },
        getPackingAuthConfig()
      );
      const pickedBox = {
        transactionId: response.data.transactionId,
        materialId: response.data.material?._id,
        batchId: response.data.batch?._id,
        materialName: response.data.material?.materialName || 'Packing material',
        barcode: response.data.barcode,
        dimensions: response.data.material?.dimensions || {},
        minimumStock: response.data.material?.minimumStock || 0,
        quantity: response.data.quantity || 1,
        status: 'PICKED',
        remainingStock: response.data.remainingStock,
        batchNumber: response.data.batch?.lotNumber,
      };
      const scannedMaterial = response.data.material?._id
        ? { ...response.data.material, currentStock: response.data.remainingStock }
        : null;
      if (scannedMaterial) {
        setAvailableBoxMaterials((previous) => previous.some((material) => String(material._id) === String(scannedMaterial._id))
          ? previous.map((material) => String(material._id) === String(scannedMaterial._id)
            ? { ...material, ...scannedMaterial }
            : material)
          : [...previous, scannedMaterial]);
        setSelectedBoxMaterials((previous) => [...previous, scannedMaterial._id]);
      }
      setBoxScans((previous) => [...previous, pickedBox]);
      setBoxBarcode('');
      setBoxScanStatus('SUCCESS');
      setExtraBoxMode(false);
      updateDashboardBoxStock(response.data.material, response.data.remainingStock);
      if (audioEnabled) playAudioFeedback('success');
      toast.current.show({ severity: 'success', summary: 'BOX SCANNED', detail: `${pickedBox.materialName} · ${pickedBox.remainingStock} remaining`, life: 2200 });
      boxBarcodeRef.current?.focus();
    } catch (error) {
      const responseData = error.response?.data || {};
      const messageText = responseData.message || 'Packing box could not be accepted.';
      setBoxScanStatus('ERROR');
      setBoxBarcode('');
      if (audioEnabled) playAudioFeedback('error');
      toast.current.show({
        severity: 'error',
        summary: responseData.code === 'OUT_OF_STOCK' ? 'OUT OF STOCK' : responseData.code === 'BOX_ALREADY_USED' ? 'BOX ALREADY USED' : responseData.code === 'INVALID_MATERIAL_TYPE' || error.response?.status === 404 ? 'INVALID PACKING MATERIAL' : 'BOX SCAN FAILED',
        detail: messageText,
        life: 3500,
      });
      boxBarcodeRef.current?.focus();
    } finally {
      boxScanInFlightRef.current = false;
      setBoxScanBusy(false);
    }
  };

  const returnScannedBox = async () => {
    if (!boxCorrection || !boxCorrectionReason.trim()) {
      toast.current.show({ severity: 'error', summary: 'Reason required', detail: 'Enter why this scanned box needs correction.', life: 2600 });
      return;
    }
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/packing/material/${encodeURIComponent(boxCorrection.transactionId)}/return`,
        {
          orderId: orderID,
          packingSessionId: orderDetails.packingSessionId,
          reason: boxCorrectionReason.trim(),
        },
        getPackingAuthConfig()
      );
      setBoxScans((previous) => previous.map((box) => box.transactionId === boxCorrection.transactionId
        ? { ...box, status: 'RETURNED', returnReason: boxCorrectionReason.trim(), remainingStock: response.data.remainingStock }
        : box));
      setBoxCorrection(null);
      setBoxCorrectionReason('');
      setBoxScanStatus('SUCCESS');
      updateDashboardBoxStock(response.data.material, response.data.remainingStock);
      toast.current.show({ severity: 'success', summary: 'Box returned', detail: 'Inventory was restored with an audit transaction.', life: 2800 });
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Correction failed', detail: error.response?.data?.message || 'Could not return this box.', life: 3200 });
    }
  };

  const releaseLockedPackingSession = async () => {
    if (!lockedOrderInfo || !adminReleaseReason.trim()) {
      toast.current.show({ severity: 'error', summary: 'Reason required', detail: 'Enter an administrator release reason.', life: 2600 });
      return;
    }
    setAdminReleaseBusy(true);
    try {
      await axios.post(
        `${config.apiBaseUrl}/packing/order-session/${encodeURIComponent(lockedOrderInfo.orderId)}/release`,
        { reason: adminReleaseReason.trim() },
        getPackingAuthConfig()
      );
      toast.current.show({ severity: 'success', summary: 'Session released', detail: 'The order is available to scan again.', life: 3000 });
      setLockedOrderInfo(null);
      setAdminReleaseReason('');
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Release failed', detail: error.response?.data?.message || 'Could not release this packing session.', life: 3200 });
    } finally {
      setAdminReleaseBusy(false);
    }
  };

  const saveUserPerformance = (data) => {
    axios
      .post(`${config.apiBaseUrl}/user-performance`, data)
      .then(() => {
      })
      .catch((err) => {
      });
  };

  const completePacking = () => {
    if (!isPackingComplete()) {
      toast.current.show({ severity: 'warn', summary: 'Products Incomplete', detail: 'Scan all required products before completing this order.', life: 3000 });
      return;
    }
    if (selectedBoxMaterials.length === 0) {
      toast.current.show({ severity: 'warn', summary: 'Packing Box Required', detail: 'Select at least one packing box type before completing this order.', life: 3000 });
      return;
    }
    const packedDate = new Date().toISOString();
    const packedTime = moment().format("HH:mm:ss");
    const packedPersonName = userName;

    const userPerformanceData = {
      user: userName,
      orderId: orderID,
      startTime,
      endTime: new Date(),
      packedDate,
      products: orderDetails.products.map((product) => ({
        name: product.name,
        sku: product.sku,
        quantity: product.quantity,
        scannedQuantity: scannedProducts[product.updated_id] || 0,
        override: scannedProducts[product.updated_id] < product.quantity,
      })),
      holdReason: null,
    };

    axios
      .post(`${config.apiBaseUrl}/order/${encodeURIComponent(orderID)}/complete-packing`, {
        packed_status: "Completed",
        packed_date: packedDate,
        packed_time: packedTime,
        packed_person_name: packedPersonName,
        packingSessionId: orderDetails.packingSessionId,
        box_type: selectedBoxMaterials.length
          ? selectedBoxMaterials
              .map((materialId) => availableBoxMaterials.find((material) => material._id === materialId))
              .filter(Boolean)
              .map((material) => material.materialName)
              .join(", ")
          : null,
      }, getPackingAuthConfig())
      .then(() => {
        if (audioEnabled) playAudioFeedback('success');
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Packing completed!",
          life: 3000,
        });
        setOrderDetails(null);
        setOrderID("");
        setScannedProducts({});
        setBoxScans([]);
        setSelectedBoxMaterials([]);
        setBoxBarcode("");
        setExtraBoxMode(false);
        setStartTime(null);
        setIsOrderPacked(false);
        setStatusMessage("Packing completed successfully. Scan a new Order ID or AWB Code.");
        setPackingStage("INITIAL");
        saveUserPerformance(userPerformanceData);
      })
      .catch((error) => {
        setStatusMessage("Failed to complete packing.");
        if (audioEnabled) playAudioFeedback('error');
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to complete packing.",
          life: 3000,
        });
      });
  };

  const holdOrder = () => {
    if (!holdReason) {
      if (audioEnabled) playAudioFeedback('error');
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide a reason for holding the order.",
        life: 3000,
      });
      return;
    }
    const packedPersonName = userName;
    const userPerformanceData = {
      user: userName,
      orderId: orderID,
      startTime,
      endTime: new Date(),
      packedDate: new Date(),
      products: orderDetails.products.map((product) => ({
        name: product.name,
        sku: product.sku,
        quantity: product.quantity,
        scannedQuantity: scannedProducts[product.updated_id] || 0,
        override: scannedProducts[product.updated_id] < product.quantity,
      })),
      holdReason,
    };

    axios
      .post(`${config.apiBaseUrl}/order/${encodeURIComponent(orderID)}/hold-packing`, {
        hold_reason: holdReason,
        reason_text: holdReason,
        packed_person_name: packedPersonName,
        packingSessionId: orderDetails.packingSessionId,
      }, getPackingAuthConfig())
      .then(() => {
        if (audioEnabled) playAudioFeedback('warning');
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Order placed on hold!",
          life: 3000,
        });
        setOrderDetails(null);
        setOrderID("");
        setScannedProducts({});
        setBoxScans([]);
        setSelectedBoxMaterials([]);
        setBoxBarcode("");
        setExtraBoxMode(false);
        setStartTime(null);
        setIsOrderPacked(false);
        setStatusMessage("Order placed on hold. Scan a new Order ID or AWB Code.");
        setPackingStage("INITIAL");
        setIsHoldModalVisible(false);
        setHoldReason("");
        saveUserPerformance(userPerformanceData);
      })
      .catch((error) => {
        if (audioEnabled) playAudioFeedback('error');
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to place order on hold.",
          life: 3000,
        });
      });
  };

  const overrideOrder = () => {
    if (!overrideReason) {
      if (audioEnabled) playAudioFeedback('error');
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide a reason for overriding the order.",
        life: 3000,
      });
      return;
    }
    if (orderDetails?.packed_status === "Completed" || orderDetails?.packed_status === "Overridden") {
      if (audioEnabled) playAudioFeedback('error');
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `Order is already ${orderDetails.packed_status.toLowerCase()}. Cannot override.`,
        life: 3000,
      });
      setIsOverrideModalVisible(false);
      return;
    }

    const packedDate = new Date().toISOString();
    const packedTime = moment().format("HH:mm:ss");
    const packedPersonName = userName;

    const overrideData = {
      packed_status: "Overridden",
      packed_date: packedDate,
      packed_time: packedTime,
      packed_person_name: packedPersonName,
      override_reason: overrideReason,
      products: orderDetails.products.map((product) => ({
        name: product.name,
        quantity: product.quantity,
        scannedQuantity: scannedProducts[product.updated_id] || 0,
      })),
      packingSessionId: orderDetails.packingSessionId,
    };

    axios
      .post(`${config.apiBaseUrl}/order/${encodeURIComponent(orderID)}/override-packing`, overrideData, getPackingAuthConfig())
      .then((response) => {
        if (response.status === 200) {
          if (audioEnabled) playAudioFeedback('warning');
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Order packing overridden successfully!",
            life: 3000,
          });
          setOrderDetails((prevDetails) => ({
            ...prevDetails,
            packed_status: "Overridden",
            packingSessionOpen: false,
          }));
          setPackingStage("OVERRIDDEN");
          setStatusMessage("Order packing overridden. Scan a new Order ID or AWB Code.");
          setOverrideReason("");
          setIsOverrideModalVisible(false);
        } else {
          if (audioEnabled) playAudioFeedback('error');
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: "Override succeeded, but status not updated correctly.",
            life: 3000,
          });
        }
      })
      .catch((error) => {
        if (audioEnabled) playAudioFeedback('error');
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: `Failed to override order packing: ${error.response?.data?.message || error.message}`,
          life: 3000,
        });
      });
  };

  const isPackingComplete = () => {
    if (!orderDetails) return false;
    return orderDetails.products.every(
      (product) => scannedProducts[product.updated_id] === product.quantity
    );
  };

  // Calculate packing progress percentage
  const calculateProgress = () => {
    if (!orderDetails || orderDetails.products.length === 0) return 0;

    const totalProducts = orderDetails.products.length;
    const totalQuantity = orderDetails.products.reduce((sum, product) => sum + product.quantity, 0);
    const totalScanned = orderDetails.products.reduce((sum, product) =>
      sum + (scannedProducts[product.updated_id] || 0), 0);

    return Math.round((totalScanned / totalQuantity) * 100);
  };

  const getStageTag = () => {
    switch (packingStage) {
      case "INITIAL":
        return <Tag color="blue">Ready to Scan</Tag>;
      case "SCANNING":
        return <Tag color="orange">Order Scanned</Tag>;
      case "PACKING":
        return <Tag color="purple">Packing in Progress</Tag>;
      case "COMPLETED":
        return <Tag color="green">Packing Completed</Tag>;
      case "ON_HOLD":
        return <Tag color="red">On Hold</Tag>;
      case "OVERRIDDEN":
        return <Tag color="cyan">Packing Overridden</Tag>;
      default:
        return <Tag color="blue">Ready to Scan</Tag>;
    }
  };

  const getTagColor = (tagName) => {
    if (!tagName) return 'rgb(65, 144, 250)';
    const t = tagName.toString().trim().toLowerCase();
    const map = {
      'vvip': '#c41d7f',
      'v vip': '#c41d7f',
      'vip': '#fa8c16',
      'loyal': '#52c41a',
      'repeat': '#2f54eb',
      'repeat customer': '#2f54eb',
      'new': '#13c2c2',
      'other': 'rgb(65, 144, 250)'
    };
    return map[t] || 'rgb(65, 144, 250)';
  };

  const getMissingProducts = () => {
    if (!orderDetails) return [];
    return orderDetails.products.filter(
      (product) => (scannedProducts[product.updated_id] || 0) < product.quantity
    );
  };

  const rowClassName = (rowData) => {
    return rowData.scanned === rowData.quantity
      ? 'row-completed'
      : 'row-pending';
  };

  // Prepare data for DataTable and calculate totals
  const tableData = orderDetails
    ? orderDetails.products.map((product) => {
        const weightMatch = product.sku?.match(/(\d+)(g|kg|ml|l)/i);
        const weight = weightMatch
          ? weightMatch[2].toLowerCase() === "ml"
            ? `${parseFloat(weightMatch[1]) / 1000} l`
            : weightMatch[2].toLowerCase() === "l"
            ? `${parseFloat(weightMatch[1])} l`
            : weightMatch[2].toLowerCase() === "kg"
            ? `${parseFloat(weightMatch[1])} kg`
            : `${parseFloat(weightMatch[1]) / 1000} kg`
          : product.weight
          ? `${product.weight} kg`
          : "Unknown";

        return {
          key: product.updated_id,
          name: product.name,
          sku: product.sku,
          quantity: product.quantity,
          scanned: scannedProducts[product.updated_id] || 0,
          status: (scannedProducts[product.updated_id] || 0) >= product.quantity ? 'Scanned' : 'Pending',
          weight: weight,
        };
      })
    : [];

  // Calculate totals for footer
  const totalProducts = tableData.length;
  const totalQuantity = tableData.reduce((sum, item) => sum + item.quantity, 0);
  const totalScanned = tableData.reduce((sum, item) => sum + item.scanned, 0);
  const totalRemaining = totalQuantity - totalScanned;
  const progress = calculateProgress();
  const pickedBoxes = boxScans.filter((box) => box.status === 'PICKED');
  const totalBoxesUsed = pickedBoxes.reduce((total, box) => total + Number(box.quantity || 1), 0);
  const selectedBox = pickedBoxes[pickedBoxes.length - 1] || null;
  const selectedInventory = selectedBox && (dashboardInventory.boxInventory || []).find((item) => String(item.materialId) === String(selectedBox.materialId));
  const selectedBoxStock = selectedInventory?.currentStock ?? selectedBox?.remainingStock;
  const selectedBoxMinimum = selectedInventory?.minimumStock ?? selectedBox?.minimumStock ?? 0;
  const selectedBoxStockState = Number(selectedBoxStock || 0) === 0 ? 'OUT OF STOCK' : Number(selectedBoxStock || 0) < Number(selectedBoxMinimum || 0) ? 'LOW STOCK' : 'IN STOCK';
  const productsComplete = Boolean(orderDetails) && orderDetails.products.every((product) => Number(scannedProducts[product.updated_id] || 0) >= Number(product.quantity || 0));

  useEffect(() => {
    if (orderDetails && totalBoxesUsed === 0) {
      setBoxScanStatus('WAITING FOR SCAN');
      const focusTimer = setTimeout(() => boxBarcodeRef.current?.focus(), 0);
      return () => clearTimeout(focusTimer);
    }
  }, [orderDetails, totalBoxesUsed]);

  // Formatted date for the landing screen (derived only, no new state)
  const todayFormatted = moment().format("DD MMM YYYY");

  // DataTable footer
  const footer = (
    <div className="p-datatable-footer">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "0.5rem 1rem",
          background: "#669da0",
          color: "#fff",
          borderRadius: "4px",
        }}
      >
        <span>Total Product Quantity: {totalQuantity}</span>
        <span style={{paddingLeft:'700px'}}>Scanned: {totalScanned} (Remaining: {totalRemaining})</span>
      </div>
    </div>
  );

  // Show the new premium landing screen only before a session/order has
  // started (packingStage === "INITIAL" and nothing scanned yet). The
  // instant the operator scans an Order ID / AWB code, the existing
  // fetchOrderDetails() logic (unchanged) moves packingStage away from
  // "INITIAL" and the original packing console renders exactly as before.
  const showSessionLanding = packingStage === "INITIAL" && !orderDetails;

  return (
    <div ref={packingRootRef} className={`packing-root ${themeClass}`}>
      <Toast ref={toast} />

      {/* Network Lost Banner */}
      {showNetworkBanner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          textAlign: 'center'
        }}>
          <Alert
            message={
              <span>
                Network Connection Lost
              </span>
            }
            description="You are currently offline. Some features may be unavailable."
            type="warning"
            showIcon
            closable
            onClose={() => setShowNetworkBanner(false)}
            style={{
              backgroundColor: '#fffbe6',
              border: '1px solid #ffe58f'
            }}
          />
        </div>
      )}

      {showSessionLanding ? (
        <div className="tgh-landing">
          <div className="tgh-landing-top">
            {/* Hero */}
            <div className="tgh-hero-card">
              <span className="tgh-badge">
                <ClockCircleOutlined /> Session Not Started
              </span>
              <Title level={1} className="tgh-hero-heading">
                Start Your Packing Session
              </Title>
              <Text className="tgh-hero-subtitle">
                Open a new packing session to begin scanning orders and processing your daily shipments.
              </Text>

              <div className="tgh-hero-illustration">
                <div className="tgh-illustration-box">
                  <InboxOutlined />
                </div>
                <div className="tgh-illustration-check">
                  <CheckCircleFilled />
                </div>
              </div>

              <div className="tgh-feature-row">
                <div className="tgh-feature-item">
                  <span className="tgh-feature-icon"><SafetyCertificateOutlined /></span>
                  <div>
                    <Text strong className="tgh-feature-title">Track Your Progress</Text>
                    <Text className="tgh-feature-desc">Monitor orders &amp; packing performance</Text>
                  </div>
                </div>
                <div className="tgh-feature-item">
                  <span className="tgh-feature-icon"><ScanOutlined /></span>
                  <div>
                    <Text strong className="tgh-feature-title">Scan &amp; Pack</Text>
                    <Text className="tgh-feature-desc">Scan orders, verify products and complete packing</Text>
                  </div>
                </div>
                <div className="tgh-feature-item">
                  <span className="tgh-feature-icon"><ThunderboltOutlined /></span>
                  <div>
                    <Text strong className="tgh-feature-title">Stay Productive</Text>
                    <Text className="tgh-feature-desc">Real-time updates and clear status</Text>
                  </div>
                </div>
              </div>
            </div>
            {/* Open Session card */}
            <div className="tgh-session-card">
              <div className="tgh-session-card-header">
                <span className="tgh-session-icon"><InboxOutlined /></span>
                <div>
                  <Title level={4} className="tgh-session-card-title">Open Packing Session</Title>
                  <Text className="tgh-session-card-subtitle">Start your session to begin packing operations</Text>
                </div>
                <Button
                  type="text"
                  className="tgh-landing-fullscreen-btn"
                  icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                  onClick={toggleFullscreen}
                  title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen before scanning'}
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen before scanning'}
                />
              </div>
              <div className="tgh-session-detail-row">
                <span className="tgh-detail-label"><HomeOutlined /> Warehouse</span>
                <span className="tgh-detail-value">Main Warehouse</span>
              </div>
              <div className="tgh-session-detail-row">
                <span className="tgh-detail-label"><CalendarOutlined /> Date</span>
                <span className="tgh-detail-value">{todayFormatted}</span>
              </div>
              <div className="tgh-session-detail-row">
                <span className="tgh-detail-label"><UserOutlined /> Operator</span>
                <span className="tgh-detail-value">{userName || "-"}</span>
              </div>

              <Button
                className="tgh-open-session-btn"
                onClick={handleOpenSessionClick}
                icon={<CaretRightOutlined />}
                block
              >
                Open Session
              </Button>
            </div>
          </div>

          <div className="tgh-landing-bottom">
            {/* Today's overview */}
            <Card className="tgh-overview-card" bordered={false}>
              <div className="tgh-overview-header">
                <span className="tgh-overview-icon"><BarChartOutlined /></span>
                <div className="tgh-overview-heading">
                  <Title level={4} className="tgh-card-title">Today's Packing Overview</Title>
                  <Text className="tgh-card-subtitle">Quick snapshot of your daily targets and progress</Text>
                </div>
                <span className="tgh-overview-date"><CalendarOutlined /> {todayFormatted}</span>
              </div>

              <div className="tgh-kpi-grid">
                <div className="tgh-kpi-card">
                  <span className="tgh-kpi-icon tgh-kpi-icon-blue"><CalendarOutlined /></span>
                  <Text className="tgh-kpi-label">Scheduled Today</Text>
                  <div className="tgh-kpi-value">{dashboardStats.scheduledForPackingToday ?? dashboardStats.todayStats?.todayOrdersCount ?? 0}</div>
                  <Text className="tgh-kpi-sub">Orders to pack</Text>
                </div>
                <div className="tgh-kpi-card">
                  <span className="tgh-kpi-icon tgh-kpi-icon-green"><InboxOutlined /></span>
                  <Text className="tgh-kpi-label">Packed Today</Text>
                  <div className="tgh-kpi-value">{dashboardStats.todayStats?.todayCompletedCount || 0}</div>
                  <Text className="tgh-kpi-sub">Orders completed</Text>
                </div>
                <div className="tgh-kpi-card">
                  <span className="tgh-kpi-icon tgh-kpi-icon-orange"><ClockCircleOutlined /></span>
                  <Text className="tgh-kpi-label">Remaining</Text>
                  <div className="tgh-kpi-value">{dashboardStats.todayPendingCount ?? dashboardStats.todayNotCompletedCount ?? 0}</div>
                  <Text className="tgh-kpi-sub">Orders pending</Text>
                </div>
                <div className="tgh-kpi-card">
                  <span className="tgh-kpi-icon tgh-kpi-icon-blue"><UserOutlined /></span>
                  <Text className="tgh-kpi-label">Active Sessions</Text>
                  <div className="tgh-kpi-value">{dashboardInventory.activePackingSessions || 0}</div>
                  <Text className="tgh-kpi-sub">Orders being packed</Text>
                </div>
                <div className="tgh-kpi-card">
                  <span className="tgh-kpi-icon tgh-kpi-icon-green"><ClockCircleOutlined /></span>
                  <Text className="tgh-kpi-label">Avg. Pack Time</Text>
                  <div className="tgh-kpi-value">{dashboardInventory.averagePackingDurationMs ? `${Math.round(dashboardInventory.averagePackingDurationMs / 60000)}m` : "-"}</div>
                  <Text className="tgh-kpi-sub">Completed sessions today</Text>
                </div>
              </div>
            </Card>

            {/* Session information */}
            <Card className="tgh-info-card" bordered={false}>
              <div className="tgh-info-header">
                <InfoCircleOutlined /> Session Information
              </div>

              <div className="tgh-info-row">
                <span className="tgh-detail-label"><UserOutlined /> Session Status</span>
                <Tag className="tgh-status-tag">Not Started</Tag>
              </div>
              <div className="tgh-info-row">
                <span className="tgh-detail-label"><ClockCircleOutlined /> Avg. Packing Time</span>
                <span className="tgh-detail-value">{dashboardInventory.averagePackingDurationMs ? `${Math.round(dashboardInventory.averagePackingDurationMs / 60000)} min` : "-"}</span>
              </div>
              <div className="tgh-info-row">
                <span className="tgh-detail-label"><InboxOutlined /> Today's Packed</span>
                <span className="tgh-detail-value">{dashboardStats.todayStats?.todayCompletedCount || 0}</span>
              </div>
              <div className="tgh-info-tip">
                <BulbOutlined /> Scan an order barcode to open a user-owned packing session.
              </div>
            </Card>
          </div>
        </div>
      ) : (
        <div className={`packing-container ${containerClass} tgh-active`}>
          <div className="tgh-active-header">
            <div className="tgh-active-header-left">
              <div className="tgh-active-title-row">
                <span className="tgh-active-title">Packing Session</span>
                <Tag color="green">ACTIVE</Tag>
              </div>
              <div className="tgh-active-meta">
                <span><UserOutlined /> {orderDetails?.packingSessionOpenedByName || userName || "-"}</span>
                <span><BarcodeOutlined /> {orderDetails?.packingSessionId || "Session pending"}</span>
                <span><HomeOutlined /> Main Warehouse</span>
              </div>
            </div>

            <div className="tgh-active-header-center">
              <Text className="tgh-timer-label">Session Started</Text>
              <div className="tgh-timer-value">
                <ClockCircleOutlined /> {startTime ? moment(startTime).format("HH:mm:ss") : currentTime}
              </div>
              <Text className="tgh-timer-sub">{startTime ? moment(startTime).format("DD MMM YYYY") : todayFormatted}</Text>
            </div>

            <div className="tgh-active-header-right">
              <span className="tgh-header-stat"><FireFilled /> {temperature}</span>
              <span className="tgh-header-stat">{isNetworkLost ? <DisconnectOutlined /> : <WifiOutlined />} {isNetworkLost ? 'Offline' : 'Online'}</span>
              <Button
                type="text"
                className="tgh-fullscreen-btn"
                icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={toggleFullscreen}
                title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen'}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              />
              <Button className="tgh-end-session-btn" onClick={clearOrder}>
                End Session
              </Button>
            </div>
          </div>

          <div className="tgh-progress-strip">
            <span className="tgh-progress-strip-label">Packing Progress</span>
            <ProgressBar value={progress} style={{ height: '7px', borderRadius: '5px' }} color={progress === 100 ? '#2fa562' : '#1f8a4d'} />
            <span className="tgh-progress-strip-pct">{progress}%</span>
            <div className="tgh-progress-strip-stats">
              <span>Scanned: {totalScanned}</span>
              <span>Remaining: {totalRemaining}</span>
              <span>Total Products: {totalQuantity}</span>
            </div>
          </div>

          <div className="tgh-workspace">
            <Card className="tgh-order-card" bordered={false}>
              <span className="tgh-order-card-title">Current Order</span>

              <div className="tgh-order-info-row">
                <span className="tgh-order-info-label">Order ID</span>
                <span className="tgh-order-info-value">{orderID || "-"}</span>
              </div>

              <div className="tgh-order-info-row">
                <span className="tgh-order-info-label">Customer</span>
                <span className="tgh-order-info-value">{orderDetails?.customer?.name || "-"}</span>
              </div>

              <div className="tgh-order-info-row">
                <span className="tgh-order-info-label">Email</span>
                <span className="tgh-order-info-value">{orderDetails?.customer?.email || "-"}</span>
              </div>

              <div className="tgh-order-info-row">
                <span className="tgh-order-info-label">Sales Count</span>
                <span className="tgh-order-info-value">{orderDetails?.customer?.sale_order_count || 0}</span>
              </div>

              <div className="tgh-order-info-row">
                <span className="tgh-order-info-label">Packing Status</span>
                <span className="tgh-order-info-value">{getStageTag()}</span>
              </div>

              {orderDetails?.status === "On Hold" && (
                <div className="tgh-order-hold-reason">Hold Reason: {orderDetails.hold_reason || "-"}</div>
              )}

              <section className="tgh-box-card tgh-customer-box-selection">
                <div className="tgh-box-card-head">
                  <span className="tgh-product-card-title"><DropboxOutlined /> Customer Box Selection</span>
                  <Tag color={selectedBoxMaterials.length ? 'green' : 'default'}>{selectedBoxMaterials.length} selected</Tag>
                </div>
                <div className="tgh-customer-selection-summary">
                  <span>Customer</span>
                  <strong>{orderDetails?.customer?.name || "-"}</strong>
                </div>
                <div className="tgh-selected-box-types">
                  {selectedBoxMaterials.length === 0 ? (
                    <span className="tgh-selected-box-types-empty">No box type selected</span>
                  ) : selectedBoxMaterials.map((materialId, index) => {
                    const material = availableBoxMaterials.find((item) => item._id === materialId);
                    return material ? (
                      <button
                        type="button"
                        key={`${material._id}-${index}`}
                        className="tgh-selected-box-type"
                        onClick={() => setSelectedBoxMaterials((previous) => previous.filter((id, itemIndex) => !(id === material._id && itemIndex === previous.lastIndexOf(material._id))))}
                        title={`Remove one ${material.materialName}`}
                      >
                        <DropboxOutlined />
                        <span>
                          <strong>{material.materialName}</strong>
                          <small>{material.materialType || "Box"}</small>
                        </span>
                        <span className="tgh-selected-box-remove">Remove</span>
                      </button>
                    ) : null;
                  })}
                </div>
                <Text className="tgh-box-scan-hint">Click a selected box to remove one copy. Click a material row to add another box of the same type.</Text>
              </section>

              <Card className="tgh-box-material-card tgh-packing-material-panel" bordered={false} title={<span className="tgh-product-card-title">Packing Material Boxes</span>}>
                <div className="tgh-box-material-header">
                  <Text>{selectedBoxMaterials.length} selected</Text>
                  <Button
                    size="small"
                    type="link"
                    disabled={loadingBoxMaterials || availableBoxMaterials.length === 0}
                    onClick={() => setSelectedBoxMaterials(
                      selectedBoxMaterials.length === availableBoxMaterials.length ? [] : availableBoxMaterials.map((material) => material._id)
                    )}
                  >
                    {selectedBoxMaterials.length === availableBoxMaterials.length ? 'Clear all' : 'Select all'}
                  </Button>
                </div>
                <div className="tgh-box-material-list">
                  {loadingBoxMaterials ? (
                    <div className="tgh-box-material-empty"><LoadingOutlined /> Loading boxes...</div>
                  ) : availableBoxMaterials.length === 0 ? (
                    <div className="tgh-box-material-empty">No active packing boxes found.</div>
                  ) : availableBoxMaterials.map((material) => {
                    const selectedCount = selectedBoxMaterials.filter((id) => id === material._id).length;
                    return (
                      <button
                        type="button"
                        key={material._id}
                        className={`tgh-box-material-item ${selectedCount > 0 ? 'selected' : ''}`}
                        onClick={() => setSelectedBoxMaterials((previous) => [...previous, material._id])}
                        aria-pressed={selectedCount > 0}
                        title={`Select another ${material.materialName}`}
                      >
                        <span className="tgh-box-material-icon"><DropboxOutlined /></span>
                        <span className="tgh-box-material-copy">
                          <strong>{material.materialName}</strong>
                          <small>{material.materialType || "Box"} · {material.materialCode} · Stock {material.currentStock ?? 0}</small>
                        </span>
                        <span className="tgh-box-material-check">{selectedCount > 0 ? `${selectedCount} SELECTED` : 'SELECT'}</span>
                      </button>
                    );
                  })}
                </div>
                <Text className="tgh-box-material-hint">Click any packing material to select it for this customer.</Text>
              </Card>

              <div className="tgh-order-actions">
                <Button onClick={clearOrder}>Clear Order</Button>
                <Button type="default" danger onClick={() => setIsHoldModalVisible(true)}>Hold Order</Button>
                <Button onClick={() => setIsOverrideModalVisible(true)}>Override Packing</Button>
              </div>
            </Card>

            <div className="tgh-packing-main">
              <Card className="tgh-product-card" bordered={false} title={<span className="tgh-product-card-title">Product Scanning</span>}>
                <div className="tgh-scanner-strip">
                  <span className={`tgh-scanner-dot ${!isNetworkLost ? 'tgh-scanner-dot-live' : ''}`} />
                  <Text className="tgh-scanner-status-text">{isNetworkLost ? 'Offline' : 'Ready to Scan'} • {statusMessage}</Text>
                </div>

                <div className="tgh-product-table-container">
                  <DataTable
                    value={tableData}
                    rowClassName={rowClassName}
                    className="tgh-product-table p-datatable-striped"
                    scrollable
                    scrollHeight="100%"
                    style={{ minWidth: "100%" }}
                  >
                    <Column
                      field="name"
                      header="Product"
                      sortable
                      style={{ width: "38%" }}
                      body={(rowData) => (
                        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                          <strong>{rowData.name}</strong>
                          <small style={{ color: '#5b7568' }}>SKU: {rowData.sku || "-"}</small>
                        </div>
                      )}
                    />
                    <Column field="sku" header="SKU / Barcode" sortable style={{ width: "22%" }} />
                    <Column field="quantity" header="Required" sortable style={{ width: "12%" }} />
                    <Column field="scanned" header="Scanned" sortable style={{ width: "12%" }} />
                    <Column
                      field="status"
                      header="Status"
                      sortable
                      style={{ width: "16%" }}
                      body={(rowData) => (
                        <Tag color={rowData.status === 'Scanned' ? 'green' : 'orange'}>
                          {rowData.status}
                        </Tag>
                      )}
                    />
                  </DataTable>
                </div>
              </Card>

            </div>
          </div>

          <div className="tgh-action-bar">
            <div className="tgh-action-bar-left">Scanned: <strong>{totalScanned}</strong> / {totalQuantity}</div>
            <div className="tgh-action-bar-center">
              <div className="tgh-action-progress">
                <ProgressBar value={progress} style={{ height: '8px', borderRadius: '5px' }} color={progress === 100 ? '#2fa562' : '#1f8a4d'} />
                <span>{progress}%</span>
              </div>
              <span className="tgh-action-box-count"><DropboxOutlined /> Selected boxes: <strong>{selectedBoxMaterials.length}</strong></span>
            </div>
            <div className="tgh-action-bar-right">
              <Button onClick={() => setIsHoldModalVisible(true)}>Hold Order</Button>
              <Button onClick={() => setIsOverrideModalVisible(true)}>Override Packing</Button>
              <Button className="tgh-complete-btn" onClick={completePacking} disabled={!productsComplete || selectedBoxMaterials.length === 0}>Complete Packing</Button>
            </div>
          </div>
        </div>
      )}

      <Modal
        title="ORDER ALREADY OPEN"
        open={Boolean(lockedOrderInfo)}
        onCancel={() => { setLockedOrderInfo(null); setAdminReleaseReason(''); }}
        footer={(
          <Space>
            {canAdminRelease && (
              <Button type="primary" danger loading={adminReleaseBusy} disabled={!adminReleaseReason.trim()} onClick={releaseLockedPackingSession}>
                Release packing session
              </Button>
            )}
            <Button onClick={() => { setLockedOrderInfo(null); setAdminReleaseReason(''); }}>Close</Button>
          </Space>
        )}
      >
        <Alert
          type="error"
          showIcon
          message={`Order ${lockedOrderInfo?.orderId || ''} is already open`}
          description={(
            <div>
              <p>This order is currently open in <strong>{lockedOrderInfo?.openedByName || 'another operator'}'s</strong> packing session.</p>
              <p>Session opened: {lockedOrderInfo?.formattedOpenedAt || 'Unknown time'}</p>
              <p>Please contact your administrator if this order needs to be released.</p>
            </div>
          )}
        />
        {canAdminRelease && (
          <Input.TextArea
            value={adminReleaseReason}
            onChange={(event) => setAdminReleaseReason(event.target.value)}
            placeholder="Administrator release reason"
            rows={2}
            style={{ marginTop: 14 }}
          />
        )}
      </Modal>

      <Modal
        title="Return scanned box"
        visible={Boolean(boxCorrection)}
        onOk={returnScannedBox}
        onCancel={() => { setBoxCorrection(null); setBoxCorrectionReason(''); }}
        okText="Return box"
        cancelText="Keep box"
        className={darkTheme ? 'dark-modal' : ''}
      >
        <Text>Returning {boxCorrection?.materialName || 'this box'} creates a stock return transaction.</Text>
        <Input.TextArea
          value={boxCorrectionReason}
          onChange={(event) => setBoxCorrectionReason(event.target.value)}
          placeholder="Reason for correction"
          rows={3}
          style={{ marginTop: 12 }}
        />
      </Modal>

      <Modal
        title="Hold Order"
        visible={isHoldModalVisible}
        onOk={holdOrder}
        onCancel={() => {
          setIsHoldModalVisible(false);
          setHoldReason("");
        }}
        okText="Submit"
        cancelText="Cancel"
        className={darkTheme ? 'dark-modal' : ''}
      >
        <Text style={{ color: darkTheme ? '#ffffff' : '#000000' }}>
          Please provide a reason for holding the order:
        </Text>
        <Input.TextArea
          value={holdReason}
          onChange={(e) => setHoldReason(e.target.value)}
          placeholder="E.g., Out of stock, waiting for approval, etc."
          rows={4}
          style={{
            marginTop: "10px",
            backgroundColor: darkTheme ? '#1f1f1f' : '#ffffff',
            color: darkTheme ? '#ffffff' : '#000000',
            borderColor: darkTheme ? '#434343' : '#d9d9d9'
          }}
        />
      </Modal>

      <Modal
        title="Override Packing"
        visible={isOverrideModalVisible}
        onOk={overrideOrder}
        onCancel={() => {
          setIsOverrideModalVisible(false);
          setOverrideReason("");
        }}
        okText="Submit Override"
        cancelText="Cancel"
        className={darkTheme ? 'dark-modal' : ''}
      >
        <Text style={{ color: darkTheme ? '#ffffff' : '#000000' }}>Missing Products:</Text>
        <List
          dataSource={getMissingProducts()}
          renderItem={(product) => (
            <List.Item style={{ color: darkTheme ? '#ffffff' : '#000000' }}>
              {product.name} - Required: {product.quantity}, Scanned: {scannedProducts[product.updated_id] || 0}
            </List.Item>
          )}
          style={{
            marginTop: "10px",
            marginBottom: "20px",
            backgroundColor: darkTheme ? '#1f1f1f' : '#ffffff'
          }}
        />
        <Text style={{ color: darkTheme ? '#ffffff' : '#000000' }}>
          Please provide a reason for overriding the order:
        </Text>
        <Input.TextArea
          value={overrideReason}
          onChange={(e) => setOverrideReason(e.target.value)}
          placeholder="E.g., Product out of stock, customer request, etc."
          rows={4}
          style={{
            marginTop: "10px",
            backgroundColor: darkTheme ? '#1f1f1f' : '#ffffff',
            color: darkTheme ? '#ffffff' : '#000000',
            borderColor: darkTheme ? '#434343' : '#d9d9d9'
          }}
        />
      </Modal>
    </div>
  );
};

export default PackingScreen;
